import { ClubResponse } from "*";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import React, { useMemo, useRef, useState } from "react";
import { classNames } from "../utils/classNames";
import { useDateRange } from "../hooks";
import ChartFilter from "./ChartFilter";
import { Chart } from "./Chartjs";
import CompareToggle from "./CompareToggle";
import { Headline, SubHeadline } from "./Headline";
import { LineChart } from "./LineChart";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import formatNumber from "../utils/formatNumber";
dayjs.extend(weekday);

export default function Checkins({ club }: { club: ClubResponse }) {
  const [showPrev, setShowPrev] = useState(false);
  const shareable = useRef(null);
  const { interval } = useDateRange();
  const checkins = club?.checkins;

  const compareLabels =
    interval === "month" ? "DD.MM.YY" : interval === "year" ? "MMM." : "dd";

  return (
    <div>
      <SubHeadline>CheckIns</SubHeadline>
      <div className="print-hidden mt-6">
        <ChartFilter
          shareable={shareable}
          headline={`Checkins: ${club?.name}`}
        />

        <CompareToggle
          compareWithPrevios={showPrev}
          setCompareWithPrevious={setShowPrev}
          currentStyle="bg-primary-500"
          compareStyle="bg-gray-400"
          visibleRange={{
            current: [club?.labels[0], club?.labels[club.labels.length - 1]],
            compare: [
              club?.compare.labels[0],
              club?.compare.labels[club.compare.labels.length - 1],
            ],
          }}
        />
      </div>
      <div ref={shareable}>
        <div className="flex gap-3 mb-6 justify-between">
          <div className="text-primary-500">
            <div className="text-sm">CheckIns im gewählten Zeitraum</div>
            <div className="flex gap-2 items-center">
              <CheckCircleIcon className="h-5 w-5 text-primary-500" />{" "}
              <span className="text-lg font-semibold">
                {formatNumber(checkins?.total)}
              </span>
            </div>
          </div>

          {checkins?.compare && showPrev && (
            <div className="text-gray-400">
              <div className="text-sm">CheckIns im Vergleichszeitraum</div>
              <div className="flex gap-2 items-center">
                <CheckCircleIcon className="h-5 w-5 text-gray-500" />{" "}
                <span className="text-lg font-semibold">
                  {formatNumber(checkins?.compare.total)}
                </span>
              </div>
            </div>
          )}
        </div>
        <Chart
          type="bar"
          options={{
            scales: {
              y: {
                grid: { display: false },
                ticks: { precision: 0, beginAtZero: true },
              },
              x: {
                grid: { display: false },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: 8,
                },
              },
            },
            datasets: {
              bar: {
                borderRadius: {
                  topLeft: 10,
                  topRight: 10,
                  bottomLeft: 0,
                  bottomRight: 0,
                },
                borderSkipped: false,
              },
            },
            plugins: {
              legend: { display: false },
            },
          }}
          data={{
            labels: checkins?.series?.map((s, i) =>
              dayjs(checkins?.labels[i]).format(compareLabels)
            ),
            datasets: [
              {
                label: "Aktueller Zeitraum",
                data: checkins?.series?.map((s, i) => ({
                  x: dayjs(checkins?.labels[i]).format(compareLabels),
                  y: s.total,
                })),
                backgroundColor: "rgba(44, 56, 127, 1)",
              },
              checkins?.compare && showPrev
                ? {
                    label: "Vorheriger Zeitraum",
                    data:
                      checkins?.compare.series?.map((s, i) => ({
                        x: dayjs(checkins?.labels[i]).format(compareLabels),
                        y: s.total,
                      })) || [],
                    backgroundColor: "#BABDC2",

                    // borderColor: "rgba(134, 194, 235, .9)",
                    // backgroundColor: "transparent",
                    // borderDash: [5, 5],
                    // pointBackgroundColor: "transparent",
                    // pointRadius: 0,
                  }
                : false,
            ].filter(Boolean),
          }}
        />

        <Weekdays club={club} />
      </div>
      {/* <div className="h-[1000px]"></div> */}
    </div>
  );
}

function Weekdays({ club }) {
  const [selectedDay, setSelectedDay] = React.useState(1);
  const occupancyAtSelectedDay = useMemo(() => {
    return club?.occupancy?.filter((hour) => hour.weekday === selectedDay);
  }, [club?.occupancy, selectedDay]);
  return (
    <>
      <SubHeadline className="my-6">Check-in-Zeiten</SubHeadline>
      <div className="flex gap-3 my-6 justify-between text-sm">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((day, i) => (
          <div
            key={day}
            onClick={() => {
              setSelectedDay(i + 1);
            }}
            className={classNames(
              selectedDay === i + 1
                ? "text-primary-400 underline"
                : " text-gray-400"
            )}
          >
            {day}
          </div>
        ))}
      </div>
      <Chart
        type="bar"
        options={{
          scales: {
            y: { display: false },
            x: {
              grid: { display: false },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 8,
              },
            },
          },
          datasets: {
            bar: {
              borderRadius: 100,
              borderSkipped: false,
            },
          },
          plugins: {
            legend: { display: false },
          },
        }}
        data={{
          labels: Array(24)
            .fill("")
            .map((_, i) => `${i} Uhr`),
          datasets: [
            {
              label: "Checkins",
              data:
                occupancyAtSelectedDay?.map(
                  ({ weekday, hour, count }) => count
                ) || [],
              backgroundColor: occupancyAtSelectedDay?.map(
                ({ hour, weekday }) =>
                  dayjs().hour() === hour && dayjs().weekday()
                    ? "rgba(44, 56, 127, 1)"
                    : "rgba(44, 56, 127, 0.5)"
              ),
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 0,
            },
          ],
        }}
      />
    </>
  );
}
